/**
 * 数据库初始化工具
 * 用于快速初始化 IndexedDB 数据
 * 其他人可以通过调用 initDatabase() 方法来重置数据
 */

import { initializeDB, isDBInitialized } from '../services/indexedDB'
import { projects, projectTimelineDetails } from '../mock/projectData'

/**
 * 初始化数据库
 * 如果数据库未初始化或需要重置，调用此方法
 * @param {boolean} force - 是否强制重新初始化（即使已有数据）
 * @returns {Promise<{success: boolean, message: string, count: number}>}
 */
export async function initDatabase(force = false) {
  try {
    // 检查是否已初始化
    const isInitialized = await isDBInitialized()

    if (isInitialized && !force) {
      console.log('数据库已初始化，跳过初始化步骤')
      return {
        success: true,
        message: '数据库已包含数据，如需重置请使用 initDatabase(true)',
        count: projects.length,
        skipped: true
      }
    }

    // 执行初始化
    await initializeDB(projects, projectTimelineDetails)

    const message = force
      ? '数据库已强制重新初始化'
      : '数据库初始化完成'

    console.log(message, `共导入 ${projects.length} 个项目`)

    return {
      success: true,
      message,
      count: projects.length,
      skipped: false
    }
  } catch (error) {
    console.error('数据库初始化失败:', error)
    return {
      success: false,
      message: `初始化失败: ${error.message}`,
      count: 0,
      error
    }
  }
}

/**
 * 检查数据库状态
 * @returns {Promise<{initialized: boolean, count: number}>}
 */
export async function checkDatabaseStatus() {
  try {
    const isInitialized = await isDBInitialized()

    if (isInitialized) {
      const { getAllProjects } = await import('../services/indexedDB')
      const allProjects = await getAllProjects()
      return {
        initialized: true,
        count: allProjects.length
      }
    }

    return {
      initialized: false,
      count: 0
    }
  } catch (error) {
    console.error('检查数据库状态失败:', error)
    return {
      initialized: false,
      count: 0,
      error: error.message
    }
  }
}

/**
 * 重置数据库
 * 清空所有数据并重新导入
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function resetDatabase() {
  return initDatabase(true)
}

/**
 * 获取项目统计数据
 * @returns {Promise<Object>}
 */
export async function getProjectStats() {
  try {
    const { getAllProjects } = await import('../services/indexedDB')
    const allProjects = await getAllProjects()

    const stats = {
      total: allProjects.length,
      byStep: {},
      byStatus: {}
    }

    // 按流程步骤统计
    const steps = ['申报书审查', '立项评审', '任务书审查', '中期审查', '年度审查', '结题报告审查']
    steps.forEach((step, index) => {
      stats.byStep[step] = allProjects.filter(p => p.currentStep === index).length
    })

    // 按状态统计
    stats.byStatus = {
      inProgress: allProjects.filter(p => p.status === 'inProgress').length,
      completed: allProjects.filter(p => p.status === 'completed').length,
      notStarted: allProjects.filter(p => p.status === 'notStarted').length
    }

    return stats
  } catch (error) {
    console.error('获取统计数据失败:', error)
    return null
  }
}

/**
 * 在控制台打印数据库使用帮助
 */
export function printHelp() {
  console.log(`
╔════════════════════════════════════════════════════════╗
║           IndexedDB 数据库初始化工具使用指南            ║
╠════════════════════════════════════════════════════════╣
║                                                        ║
║  1. 初始化数据库（首次使用）:                           ║
║     await initDatabase()                               ║
║                                                        ║
║  2. 强制重新初始化（清空并重新导入）:                    ║
║     await initDatabase(true)                           ║
║     或                                                 ║
║     await resetDatabase()                              ║
║                                                        ║
║  3. 检查数据库状态:                                     ║
║     await checkDatabaseStatus()                        ║
║                                                        ║
║  4. 查看项目统计:                                       ║
║     await getProjectStats()                            ║
║                                                        ║
║  5. 在浏览器控制台快速初始化:                           ║
║     import { initDatabase } from '@/utils/dbInitializer' ║
║     await initDatabase()                               ║
║                                                        ║
╚════════════════════════════════════════════════════════╝
`)
}

// 自动打印帮助信息
printHelp()

export default {
  initDatabase,
  checkDatabaseStatus,
  resetDatabase,
  getProjectStats,
  printHelp
}
